import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ProgressBar } from 'primeng/progressbar';

export type BdUiProgressStepState = 'done' | 'running' | 'waiting' | 'failed';

export interface BdUiProgressStep {
  label: string;
  /** Shown after the label in parentheses, e.g. "Fälle 70.000 von 200.000 importiert". */
  detail?: string;
  state: BdUiProgressStepState;
  /** The running step's own percentage; `null`/omitted → indeterminate bar. */
  percent?: number | null;
}

const STATE_ICON: Readonly<Record<BdUiProgressStepState, string>> = {
  done: 'pi pi-check',
  running: 'pi pi-spin pi-spinner',
  waiting: 'pi pi-circle',
  failed: 'pi pi-times-circle',
};

/**
 * Steps of a running process with the progress inside the running step: done
 * steps checked, the running one with its percentage and a thin bar under it,
 * upcoming ones dimmed. The percentage belongs to that step only — a process
 * whose steps report their own progress has no honest overall value — so the
 * header counts steps instead ("Schritt 2 von 4") — when the caller knows
 * the total and passes `totalSteps`.
 *
 * No layout shift while steps advance or the process finishes: every row
 * keeps the bar's slot and stays on one line (the full text is its tooltip),
 * so a row is as high running as done.
 *
 * A `[stepProgressAction]` element (e.g. the log export) goes to the end of
 * the header:
 *
 * ```html
 * <bd-ui-step-progress heading="…" [steps]="steps">
 *   <bd-export-as-file stepProgressAction … />
 * </bd-ui-step-progress>
 * ```
 */
@Component({
  selector: 'bd-ui-step-progress',
  imports: [DecimalPipe, ProgressBar],
  template: `
    <div class="step-progress" role="log">
      <div class="s-header">
        @if (heading()) {
          <span class="s-title" [title]="heading()">{{ heading() }}</span>
        }
        @if (position(); as position) {
          <span class="s-position"
            >Schritt {{ position.current }} von {{ position.total }}</span
          >
        }
        <ng-content select="[stepProgressAction]" />
      </div>
      <ol class="s-steps">
        @for (step of steps(); track $index) {
          <li
            [class]="'s-' + step.state"
            [attr.aria-current]="step.state === 'running' ? 'step' : null"
          >
            <i [class]="icons[step.state] + ' s-icon'" aria-hidden="true"></i>
            <span class="s-label" [title]="text(step)">
              {{ step.label }}
              @if (step.detail) {
                <span class="s-detail">({{ step.detail }})</span>
              }
            </span>
            @if (step.state === 'running') {
              @let percent = step.percent ?? null;
              @if (percent !== null) {
                <strong class="s-percent"
                  >{{ percent | number: '1.0-0' }} %</strong
                >
              } @else {
                <span></span>
              }
              <p-progressbar
                class="s-bar"
                [mode]="percent !== null ? 'determinate' : 'indeterminate'"
                [value]="percent ?? 0"
                [showValue]="false"
                [style]="{ height: '4px' }"
                [attr.aria-label]="step.label"
              />
            } @else {
              <span></span>
              <span class="s-bar" aria-hidden="true"></span>
            }
          </li>
        }
      </ol>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .step-progress {
        padding: 24px;
        border-radius: 8px;
        background: var(--p-content-hover-background);
        color: var(--p-text-color);
      }
      .s-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }
      .s-title {
        flex: 1;
        min-width: 0;
        font-weight: 600;
      }
      .s-title,
      .s-label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .s-label {
        min-width: 0;
      }
      .s-position {
        flex: none;
        font-size: 0.75rem;
        color: var(--p-text-muted-color);
      }
      .s-steps {
        margin: 0.75rem 0 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 0.5rem;
        font-size: 0.875rem;
      }
      .s-steps li {
        display: grid;
        grid-template-columns: 1rem 1fr auto;
        column-gap: 0.5rem;
        align-items: center;
      }
      .s-icon {
        font-size: 0.875rem;
        line-height: 1;
      }
      .s-done {
        color: var(--p-text-muted-color);
      }
      .s-done .s-icon {
        color: var(--p-bd-status-success-color);
      }
      .s-running .s-icon {
        color: var(--p-primary-color);
      }
      .s-waiting {
        color: var(--p-text-muted-color);
        opacity: 0.6;
      }
      .s-waiting .s-icon {
        font-size: 0.5rem;
        justify-self: center;
      }
      /* The shared error ink (p-message, bd-ui-inline-notification): a failed
         step and its notification match. */
      .s-failed,
      .s-failed .s-icon {
        color: var(--p-bd-status-error-color);
      }
      .s-detail {
        color: var(--p-text-muted-color);
      }
      .s-percent {
        font-variant-numeric: tabular-nums;
      }
      .s-bar {
        display: block;
        grid-column: 2 / 4;
        height: 4px;
        margin-top: 0.25rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdUiStepProgressComponent {
  public readonly heading = input<string>();
  public readonly steps = input.required<readonly BdUiProgressStep[]>();
  /**
   * Number of steps the process has in all, for "Schritt n von m". Omit it
   * when the process does not know its upcoming steps: the header then shows
   * no count.
   */
  public readonly totalSteps = input<number>();

  protected readonly icons = STATE_ICON;

  protected text(step: BdUiProgressStep): string {
    return step.detail ? `${step.label} (${step.detail})` : step.label;
  }

  /** 1-based position of the running step; `null` without one, without a total or with a single step. */
  protected readonly position = computed(() => {
    const total = this.totalSteps();
    const index = this.steps().findIndex((step) => step.state === 'running');
    return total === undefined || total < 2 || index < 0
      ? null
      : { current: index + 1, total };
  });
}
